// Detail overlay — lot (origin globe behind, drawn by the GL stage) or dish
// (photo). Fade 420 ms, beats 140 ms later. Swipe down closes; on dishes a
// horizontal swipe steps; on lots a horizontal drag spins the globe.
// Port of prototype lines 302–323 and detailView (463–467).
import { useRef } from 'react';
import { flat } from './data/adapt.js';
import { SIZES, srcSet } from './data/photos.js';

const mono = { fontFamily: "'DM Mono', 'DM Mono Fallback', ui-monospace, monospace" };

export default function Detail({ detail, T, fs, vis, on, gl, onClose, onStep, glRef }) {
  const pd = useRef(null);
  const { pg, ci, li, kind } = detail;
  const lot = kind === 'lot';
  const items = flat(pg);
  const i = Math.max(0, items.findIndex((x) => x.ci === ci && x.li === li));
  const it = items[i] || items[0];
  const prev = items[(i - 1 + items.length) % items.length];
  const next = items[(i + 1) % items.length];
  const c = it.c;
  const ink = '#F5F3EB', accent = '#e8b86b';
  const muted = lot ? '#b9c1bf' : '#D4C4A6';
  const faint = lot ? '#8FA09E' : '#D4C4A6';
  const solid = lot ? '#071e21' : '#234A3F';
  const bg = lot ? (gl ? 'transparent' : '#071e21') : '#234A3F';
  const line1 = lot ? (c.origin || '') : (c.signature ? `◆ ${T.house}` : '');
  const addons = c.addons ? c.addons.map((a) => `${a.label} ${a.price}`).join(' · ') : '';

  const onDown = (e) => { pd.current = { x: e.clientX, y: e.clientY, lx: e.clientX }; };
  const onMove = (e) => {
    if (!pd.current) return;
    const dx = e.clientX - pd.current.lx;
    pd.current.lx = e.clientX;
    if (lot && glRef.current) glRef.current.globe.spinV += dx * 0.0035;
  };
  const onUp = (e) => {
    if (!pd.current) return;
    const dy = e.clientY - pd.current.y, dx = e.clientX - pd.current.x;
    pd.current = null;
    if (dy > 90 && Math.abs(dx) < 70) onClose();
    else if (!lot && Math.abs(dx) > 70 && Math.abs(dy) < 60) onStep(dx < 0 ? 1 : -1);
  };

  return (
    <div
      data-detail
      data-on={on}
      role="dialog"
      aria-modal="true"
      aria-label={c.name}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
      style={{ position: 'absolute', inset: 0, zIndex: 20, background: bg, color: ink, display: 'flex', flexDirection: 'column', fontSize: fs + 'px', lineHeight: 1.35, opacity: vis, transition: 'opacity .42s cubic-bezier(.2,.7,.2,1)', touchAction: 'none', overscrollBehavior: 'contain', overflow: 'hidden' }}
    >
      {c.photo ? (
        <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: '56%' }}>
          <img src={c.photo} srcSet={srcSet(c.photo)} sizes={SIZES} alt={c.name} decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transform: 'translate3d(calc(var(--tx, 0) * -8px), calc(var(--ty, 0) * -6px), 0) scale(1.06)' }} />
        </div>
      ) : null}
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: `linear-gradient(180deg, rgba(0,0,0,0) 30%, ${solid} 60%)`, pointerEvents: 'none' }} />

      <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 8px 0 20px' }}>
        <span style={{ ...mono, fontSize: '.6875em', letterSpacing: '.14em', textTransform: 'uppercase', color: accent, flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{T.r[pg.runner]} · {it.cat.label}</span>
        <button type="button" onClick={onClose} aria-label={T.close} style={{ width: 44, height: 44, display: 'grid', placeItems: 'center', borderRadius: '50%', border: '1px solid rgba(244,240,232,.45)' }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true"><path d="M2 2l10 10M12 2 2 12" /></svg>
        </button>
      </div>
      {lot ? <span style={{ position: 'relative', display: 'block', padding: '6px 20px 0', ...mono, fontSize: '.625em', letterSpacing: '.12em', textTransform: 'uppercase', color: muted }}>{T.turn}</span> : null}

      <div style={{ position: 'relative', marginTop: 'auto', padding: '0 1.5em 1.5em', display: 'flex', flexDirection: 'column', gap: '.7em', transform: 'translate3d(calc(var(--tx, 0) * 6px), calc(var(--ty, 0) * 4px), 0)' }}>
        {line1 ? <span className="beat" style={{ ...mono, fontSize: '.6875em', letterSpacing: '.12em', textTransform: 'uppercase', color: accent }}>{line1}</span> : null}
        <span className="beat" role="heading" aria-level={2} style={{ fontSize: '2.4em', fontWeight: 500, letterSpacing: '-.04em', lineHeight: 0.96, textWrap: 'balance', transitionDelay: '.1s' }}>{c.name}</span>
        {c.credit ? <span className="beat" style={{ ...mono, fontSize: '.6875em', letterSpacing: '.01em', lineHeight: 1.5, color: faint, transitionDelay: '.35s' }}>{c.credit}</span> : null}
        {c.notes ? <span className="beat" style={{ fontSize: '1.125em', lineHeight: 1.4, maxWidth: '22em', textWrap: 'pretty', transitionDelay: '.5s' }}>{c.notes}</span> : null}
        <div className="beat" style={{ display: 'flex', flexDirection: 'column', gap: '.5em', marginTop: '.2em', transitionDelay: '.7s' }}>
          <span style={{ ...mono, fontWeight: 500, fontSize: '2.2em', letterSpacing: '-.02em', lineHeight: 1 }}>{c.price}</span>
          {lot ? <span style={{ ...mono, fontSize: '.6875em', letterSpacing: '.06em', color: muted }}>{pg.modifier}</span> : null}
        </div>
        {addons ? <span className="beat" style={{ ...mono, fontSize: '.75em', letterSpacing: '.02em', lineHeight: 1.5, color: accent, transitionDelay: '.8s' }}>{addons}</span> : null}
        {/* The MSG note belongs to the kitchen, not to a pastry or a pot of tea.
            pg.foot is true only on the five kitchen pages. */}
        {!lot && pg.foot ? <span className="beat" style={{ fontSize: '.9375em', color: muted, transitionDelay: '.9s' }}>{T.msg}</span> : null}
        <div className="beat" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: '.8em', transitionDelay: '1s' }}>
          <button type="button" onClick={() => onStep(-1)} style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 10, ...mono, fontSize: '.6875em', letterSpacing: '.08em', textTransform: 'uppercase', maxWidth: '46%', textAlign: 'left' }}>
            <svg width="8" height="12" viewBox="0 0 8 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}><path d="M6 1L1 6l5 5" /></svg>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{prev.c.name}</span>
          </button>
          <button type="button" onClick={() => onStep(1)} style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 10, ...mono, fontSize: '.6875em', letterSpacing: '.08em', textTransform: 'uppercase', maxWidth: '46%', textAlign: 'right', marginLeft: 'auto' }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{next.c.name}</span>
            <svg width="8" height="12" viewBox="0 0 8 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}><path d="M2 1l5 5-5 5" /></svg>
          </button>
        </div>
      </div>
    </div>
  );
}
